import { useField } from "formik";
import { Checkbox as MantineCheckbox } from "@mantine/core";
import { componentWrapper } from "../../utilities";
import styles from "./checkbox.module.scss";
import type {
    CustomCheckboxFactory,
    CustomCheckboxFormikFactory,
    ICheckboxFormikProps,
    IProps,
} from "./checkbox.types";
import { kebabCase } from "lodash";

/**
 * Checkbox component for binary selection.
 * @returns {JSX.Element} The Checkbox component.
 */
const Checkbox = componentWrapper<CustomCheckboxFactory, IProps>("Checkbox", (props, ref) => {
    return (
        <MantineCheckbox
            ref={ref}
            classNames={{ root: styles["root"], label: styles["label"], input: styles["input"] }}
            {...props}
        />
    );
});

/**
 * Checkbox component integrated with Formik for form handling and validation.
 * @param {string} props.name - The name of the checkbox field for Formik.
 * @param {function} props.validate - Custom validation function for the field.
 * @returns {JSX.Element} The CheckboxFormik component.
 */
const CheckboxFormik = componentWrapper<CustomCheckboxFormikFactory, ICheckboxFormikProps>(
    "CheckboxFormik",
    ({ name, validate, ...otherProps }, ref) => {
        const [field, meta] = useField({ name, type: "checkbox", validate });
        return (
            <Checkbox
                classNames={{ root: styles["root"], label: styles["label"], input: styles["input"] }}
                ref={ref}
                data-testid={`checkbox-${kebabCase(name)}`}
                {...otherProps}
                {...field}
                error={meta.touched ? meta.error : undefined}
            />
        );
    }
);

Checkbox.Card = MantineCheckbox.Card;
Checkbox.Group = MantineCheckbox.Group;
Checkbox.Indicator = MantineCheckbox.Indicator;
Checkbox.Formik = CheckboxFormik;

export { Checkbox, CheckboxFormik };
